import type { ErrorBody, paths } from "@dynamic-rag/contracts";
import createClient from "openapi-fetch";

/** Typed client generated from the backend's OpenAPI document (API-08). Same origin; the
 * absolute origin also keeps requests valid outside a browser (tests, server rendering). */
export const api = createClient<paths>({
  baseUrl: typeof window === "undefined" ? "" : window.location.origin,
  // Look fetch up on every call (not once at import), so a fetch patched later is used.
  fetch: (request: Request) => globalThis.fetch(request),
});

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly requestId: string | null;

  constructor(status: number, body?: Partial<ErrorBody>) {
    super(body?.message ?? `The request failed (${status}).`);
    this.name = "ApiError";
    this.status = status;
    this.code = body?.code ?? (status === 0 ? "NETWORK_ERROR" : "HTTP_ERROR");
    this.details = (body?.details as Record<string, unknown> | undefined) ?? {};
    this.requestId = body?.request_id ?? null;
  }
}

/** What the server answers when the browser is not signed in (rag_api/auth.py). A wrong
 * password is INVALID_CREDENTIALS instead, so a failed sign-in is not mistaken for one. */
const SIGNED_OUT_CODES = new Set(["AUTH_REQUIRED", "SESSION_ENDED"]);
const signedOutListeners = new Set<(error: ApiError) => void>();

export function isSignedOut(error: unknown): error is ApiError {
  return error instanceof ApiError && error.status === 401 && SIGNED_OUT_CODES.has(error.code);
}

/** Calls `listener` whenever a request finds the browser signed out, such as after the session
 * was ended on the server. Returns the function that stops it. */
export function onSignedOut(listener: (error: ApiError) => void): () => void {
  signedOutListeners.add(listener);
  return () => {
    signedOutListeners.delete(listener);
  };
}

/** Every failed request comes through here: the typed client, the chat stream and uploads. */
export function apiErrorFrom(status: number, payload: unknown): ApiError {
  const body =
    payload && typeof payload === "object" && "error" in payload
      ? (payload as { error: ErrorBody }).error
      : undefined;
  const error = new ApiError(status, body);
  if (isSignedOut(error)) for (const listener of signedOutListeners) listener(error);
  return error;
}

type FetchResult<T> = { data?: T; error?: unknown; response: Response };

/** Resolve an openapi-fetch call to its data, or throw an `ApiError`. */
export async function unwrap<T>(call: Promise<FetchResult<T>>): Promise<T> {
  let result: FetchResult<T>;
  try {
    result = await call;
  } catch {
    throw new ApiError(0, {
      code: "NETWORK_ERROR",
      message: "Can't reach the server. Check that the API is running.",
    });
  }
  if (result.error !== undefined || !result.response.ok) {
    throw apiErrorFrom(result.response.status, result.error);
  }
  return result.data as T;
}

export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return "Something went wrong.";
}
